import { IconAlert } from '@belong/icons';
import { Button, Divider, Text } from '@belong/ui';
import type { ConsumerFailedPaymentModel } from './failed-payments.types';
import { FAILED_PAYMENTS_STRINGS } from './failed-payments-strings';
import {
  formatFailedPaymentsAmount,
  getFailedPaymentsCount,
  getFailedPaymentsTotal,
} from './failed-payments-utils';

type FailedPaymentsSectionProps = {
  failedPayments: ConsumerFailedPaymentModel[];
  onResolveClick: () => void;
  showHomeownerTimeline?: boolean;
  title?: string;
};

const ICONS_BASE_URL =
  'https://belong-consumer-assets.s3.us-west-1.amazonaws.com/accounts';

const HOMEOWNER_TIMELINE = [
  {
    day: FAILED_PAYMENTS_STRINGS['section.homeowner.timeline.day_30'],
    status: FAILED_PAYMENTS_STRINGS['section.homeowner.timeline.day_30_status'],
    description:
      FAILED_PAYMENTS_STRINGS['section.homeowner.timeline.day_30_description'],
    icon: `${ICONS_BASE_URL}/failed_payment_ic_01.svg`,
  },
  {
    day: FAILED_PAYMENTS_STRINGS['section.homeowner.timeline.day_60'],
    status: FAILED_PAYMENTS_STRINGS['section.homeowner.timeline.day_60_status'],
    description:
      FAILED_PAYMENTS_STRINGS['section.homeowner.timeline.day_60_description'],
    icon: `${ICONS_BASE_URL}/failed_payment_ic_02.svg`,
  },
  {
    day: FAILED_PAYMENTS_STRINGS['section.homeowner.timeline.day_90'],
    status: FAILED_PAYMENTS_STRINGS['section.homeowner.timeline.day_90_status'],
    description:
      FAILED_PAYMENTS_STRINGS['section.homeowner.timeline.day_90_description'],
    icon: `${ICONS_BASE_URL}/failed_payment_ic_03.svg`,
  },
] as const;

function HomeownerPaymentTimeline() {
  return (
    <div
      className="flex w-full flex-col gap-xl text-navy"
      data-testid="homeowner-payment-timeline"
    >
      <div className="flex flex-col gap-xs">
        <Text
          variant="body"
          fontWeight="semibold"
          className="leading-[normal] tracking-sm"
        >
          {FAILED_PAYMENTS_STRINGS['section.homeowner.consequences.title']}
        </Text>
        <Text variant="body" className="text-p1 leading-[normal]">
          {
            FAILED_PAYMENTS_STRINGS[
              'section.homeowner.consequences.description'
            ]
          }
        </Text>
      </div>

      <div className="flex w-full flex-col">
        {HOMEOWNER_TIMELINE.map((item, index) => {
          const hasConnector = index < HOMEOWNER_TIMELINE.length - 1;

          return (
            <div className="flex items-stretch gap-sm px-2sm" key={item.day}>
              <div className="flex w-xl shrink-0 flex-col items-center">
                <div className="flex h-xl w-xl shrink-0 items-center justify-center rounded-full bg-red/20">
                  <img
                    alt=""
                    className="h-[70%] w-[70%] object-contain"
                    src={item.icon}
                  />
                </div>
                {hasConnector && (
                  <div className="min-h-xl w-0 grow border-0 border-l border-dashed border-gray" />
                )}
              </div>

              <div
                className={
                  hasConnector ? 'min-w-0 flex-1 pb-xl' : 'min-w-0 flex-1'
                }
              >
                <div className="flex flex-wrap gap-x-xs text-body font-semibold leading-[normal] tracking-sm">
                  <span>{item.day}</span>
                  <span className="text-red">{item.status}</span>
                </div>
                <Text
                  variant="body"
                  className="mt-xs text-p1 leading-[normal] tracking-sm"
                >
                  {item.description}
                </Text>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function FailedPaymentsSection({
  failedPayments,
  onResolveClick,
  showHomeownerTimeline = false,
  title = FAILED_PAYMENTS_STRINGS['section.title'],
}: FailedPaymentsSectionProps) {
  if (!failedPayments.length) return null;

  const total = getFailedPaymentsTotal(failedPayments);
  const count = getFailedPaymentsCount(failedPayments) || failedPayments.length;
  const canResolve = failedPayments.some((p) => p.canSelfResolve);
  const paymentLabel = count === 1 ? 'payment' : 'payments';

  return (
    <section
      className="flex flex-col items-end gap-sm rounded border border-solid border-red bg-[rgba(255,86,97,0.02)] p-sm"
      data-testid="failed-payments-section"
    >
      <div className="flex w-full flex-col gap-xs text-navy">
        <div className="flex w-full flex-col items-start gap-xs md:flex-row">
          <IconAlert
            className="shrink-0 text-red md:mt-2xs"
            height={24}
            width={26}
          />
          <Text
            variant="body"
            fontWeight="semibold"
            className="text-h3-sm leading-[normal] tracking-sm text-red md:text-h3-xl"
          >
            {title}
          </Text>
        </div>

        <Text
          variant="body"
          className="text-p1 leading-[normal] text-navy md:text-body"
        >
          <span className="font-semibold">{`${count} ${paymentLabel} totaling ${formatFailedPaymentsAmount(
            total,
          )} ${FAILED_PAYMENTS_STRINGS['section.summary.need_attention']} `}</span>
          {FAILED_PAYMENTS_STRINGS['section.summary.body']}{' '}
          {FAILED_PAYMENTS_STRINGS['section.summary.resolve']}
        </Text>
      </div>

      {showHomeownerTimeline && (
        <>
          <Divider borderStyle="dashed" />
          <HomeownerPaymentTimeline />
        </>
      )}

      <div className="flex w-full justify-end">
        <Button
          size={{ _: 'fluid', sm: 'default' }}
          className="rounded"
          onClick={onResolveClick}
          disabled={!canResolve}
        >
          {FAILED_PAYMENTS_STRINGS['section.cta']}
        </Button>
      </div>
    </section>
  );
}
